import { LOGIC_CATEGORIES, LOGIC_BLOCKS_RULESET } from '../logic';
import { getKeys, getValues } from '@morph-mapper/utils';
import { type SchemaVariant } from '@morph-mapper/types';
import { LogicBlock } from '../types';

/**
 * This hook provides an external interface to the logic blocks configuration.
 * It is used to dyanmically fetch the implementation of a logic block based on the schema variant.
 */
export const useLogicBlocks = (variant: SchemaVariant) => {
  /**
   * @param categories Takes a record of category names and their display names
   * @param blocks Takes a record of logic blocks
   * @returns A record of category names and their respective logic blocks
   */
  const mapBlocksByCategory = <
    TCategories extends Record<string, string>,
    TBlocks extends Record<string, Pick<LogicBlock, 'category'>>
  >(
    categories: TCategories,
    blocks: TBlocks
  ) => {
    type Block = TBlocks[keyof TBlocks];
    type CategoryKey = keyof TCategories;

    const mappedBlocks = getKeys(categories).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {} as Record<CategoryKey, Block[]>);

    getValues(blocks).forEach((block) => {
      if (block.category === 'internal') return;

      const category = block.category as CategoryKey;
      mappedBlocks[category].push(block);
    });

    return mappedBlocks;
  };

  const logicBlocks = LOGIC_BLOCKS_RULESET[variant];
  const logicBlocksByCategory = mapBlocksByCategory(
    LOGIC_CATEGORIES,
    LOGIC_BLOCKS_RULESET[variant]
  );

  return { logicBlocks, logicBlocksByCategory };
};
